import { useTranslation } from 'react-i18next';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The "if something looks off, contact…" line appended to every dunning
 * reassurance, resolved for the viewer. Two independent rules:
 *
 * 1. **White-label** (`brandingData.isWhiteLabel` — the "remove OptiSigns
 *    branding" setting): the OptiSigns support line names our address, so it must
 *    never appear on a branded portal. For a top-role viewer there, we drop the
 *    line entirely (they reach us through their own channel).
 *
 * 2. **Role**: only an Account Owner or Super Admin can actually resolve a billing
 *    problem, so they get the support line. An Admin or User is pointed at their
 *    own Owner/Super Admin instead — on every portal, branded or not (this line
 *    names no OptiSigns address, so it is white-label-safe by construction).
 *
 * Returns the translated line, or null when nothing should be shown.
 */
export function useDunningContactLine(): string | null {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);

  const role = me?.rootRole;
  const isTopRole = role === ROLE_TYPES.OWNER || role === ROLE_TYPES.SUPER_ADMIN;
  const isWhiteLabel = me?.brandingData?.isWhiteLabel === true;

  if (isTopRole) {
    // White-label top-role viewer: omit the OptiSigns support reference entirely.
    return isWhiteLabel ? null : t('dunning.contact.support');
  }
  // Admin / User (any portal): route to their own Owner / Super Admin.
  return t('dunning.contact.admin');
}
